import React from 'react';

interface ImageZoomProps {
  src: string;
  alt: string;
  onMouseLeave?: () => void;
  onReset?: () => void;
  showResetButton?: boolean;
}

const ImageZoom: React.FC<ImageZoomProps> = ({
  src,
  alt,
  onMouseLeave,
  onReset,
  showResetButton = false
}) => {
  const [zoom, setZoom] = React.useState(1);
  const [translateX, setTranslateX] = React.useState(0);
  const [translateY, setTranslateY] = React.useState(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragStartPos, setDragStartPos] = React.useState({ x: 0, y: 0 });
  const [transformOrigin, setTransformOrigin] = React.useState('center center');
  const [imageAspectRatio, setImageAspectRatio] = React.useState('9 / 16');

  const imageRef = React.useRef<HTMLImageElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const scrollableParentRef = React.useRef<HTMLElement | null>(null);
  const getCursorStyle = () => {
    if (zoom > 1) {
      return isDragging ? 'cursor-grabbing' : 'cursor-grab';
    }
    return 'cursor-zoom-in';
  };

  const resetZoom = () => {
    setZoom(1);
    setTranslateX(0);
    setTranslateY(0);
    setTransformOrigin('center center');
    onReset?.();
  };

  const handleImageLoad = () => {
    const image = imageRef.current;

    if (!image || !image.naturalWidth || !image.naturalHeight) return;

    setImageAspectRatio(`${image.naturalWidth} / ${image.naturalHeight}`);
  };

  const calculateTransformOrigin = (event: React.WheelEvent<HTMLImageElement>) => {
    if (!imageRef.current) return 'center center';

    const rect = imageRef.current.getBoundingClientRect();

    // Calcular posição relativa do mouse na imagem
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    return `${Math.min(100, Math.max(0, x))}% ${Math.min(100, Math.max(0, y))}%`;
  };

  const handleMouseEnterImage = () => {
    // Encontrar apenas o container scrollável mais próximo (pai direto ou ascendentes próximos)
    let parent = containerRef.current?.parentElement;
    let scrollableParent: HTMLElement | null = null;

    // Procurar apenas até 3 níveis acima
    for (let i = 0; i < 3 && parent; i++) {
      const computedStyle = window.getComputedStyle(parent);
      const overflowY = computedStyle.overflowY;

      if (overflowY === 'auto' || overflowY === 'scroll') {
        scrollableParent = parent;
        break;
      }
      parent = parent.parentElement;
    }

    if (scrollableParent) {
      scrollableParentRef.current = scrollableParent;
    }
  };

  const handleMouseLeaveImage = () => {
    if (scrollableParentRef.current) {
      scrollableParentRef.current.style.overflow = '';
      scrollableParentRef.current = null;
    }
  };

  const handleWheel = (
  event: React.WheelEvent<HTMLImageElement>
) => {

  // Só executa se for a imagem
  if (
    event.target !== imageRef.current
  ) {
    return;
  }
  if (!event.shiftKey) {
  return;
}

  // 🚀 SEM SHIFT:
  // deixa scroll normal funcionar
 if (!event.shiftKey) {

  return;
}

  // 🚀 COM SHIFT:
  // ativa zoom
 if (event.shiftKey) {

  event.preventDefault();
}

  const isScrollUp =
    event.deltaY < 0;

  const delta =
    isScrollUp ? 0.1 : -0.1;

  const newZoom = Math.min(
    2,
    Math.max(
      1,
      zoom + delta
    )
  );

  if (newZoom !== zoom) {

    const newOrigin =
      calculateTransformOrigin(
        event
      );

    setTransformOrigin(
      newOrigin
    );

    setZoom(

      parseFloat(
        newZoom.toFixed(2)
      )
    );
  }
};

  const handleMouseDown = (e: React.MouseEvent<HTMLImageElement>) => {
    if (zoom <= 1) return;

    e.preventDefault();
    setIsDragging(true);
    setDragStartPos({
      x: e.clientX - translateX,
      y: e.clientY - translateY,
    });
  };

  const handleDoubleClick = (e: React.MouseEvent<HTMLImageElement>) => {
    e.preventDefault();
    resetZoom();
  };

  // Preparação para mobile - eventos touch básicos
  const handleTouchStart = (e: React.TouchEvent<HTMLImageElement>) => {
    if (zoom <= 1) return;

    if (e.touches.length === 1) {
      // Single touch - iniciar drag
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStartPos({
        x: touch.clientX - translateX,
        y: touch.clientY - translateY,
      });
    }
    // TODO: Implementar pinch zoom quando e.touches.length === 2
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLImageElement>) => {
    if (!isDragging || zoom <= 1 || e.touches.length !== 1) return;

    const touch = e.touches[0];
    const container = containerRef.current;
    const image = imageRef.current;
    if (!container || !image) return;

    const containerRect = container.getBoundingClientRect();
    const imageRect = image.getBoundingClientRect();

    // Mesma lógica de limites do mouse move
    const scaledImageWidth = imageRect.width;
    const scaledImageHeight = imageRect.height;

    // Limites mais flexíveis: permite até 40% da imagem sair da tela
    const maxTranslateX = (scaledImageWidth - containerRect.width) / 2 + (scaledImageWidth * 0.4);
    const maxTranslateY = (scaledImageHeight - containerRect.height) / 2 + (scaledImageHeight * 0.4);

    const newX = touch.clientX - dragStartPos.x;
    const newY = touch.clientY - dragStartPos.y;

    const constrainedX = Math.min(maxTranslateX, Math.max(-maxTranslateX, newX));
    const constrainedY = Math.min(maxTranslateY, Math.max(-maxTranslateY, newY));

    setTranslateX(constrainedX);
    setTranslateY(constrainedY);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLImageElement>) => {
    if (!isDragging || zoom <= 1) return;

    const container = containerRef.current;
    const image = imageRef.current;
    if (!container || !image) return;

    const containerRect = container.getBoundingClientRect();
    const imageRect = image.getBoundingClientRect();

    // Calcular limites baseados no zoom atual - mais permissivos
    // Permite arrastar até 50% da imagem zoomada para fora da tela
    const scaledImageWidth = imageRect.width;
    const scaledImageHeight = imageRect.height;

    // Limites mais flexíveis: permite até 40% da imagem sair da tela
    const maxTranslateX = (scaledImageWidth - containerRect.width) / 2 + (scaledImageWidth * 0.4);
    const maxTranslateY = (scaledImageHeight - containerRect.height) / 2 + (scaledImageHeight * 0.4);

    const newX = e.clientX - dragStartPos.x;
    const newY = e.clientY - dragStartPos.y;

    // Aplicar limites mais suaves
    const constrainedX = Math.min(maxTranslateX, Math.max(-maxTranslateX, newX));
    const constrainedY = Math.min(maxTranslateY, Math.max(-maxTranslateY, newY));

    setTranslateX(constrainedX);
    setTranslateY(constrainedY);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleContainerMouseLeave = () => {
    setIsDragging(false);
    handleMouseLeaveImage();
    onMouseLeave?.();
  };

  React.useEffect(() => {

  const handleKeyUp = (

    event: KeyboardEvent

  ) => {

    if (event.key === 'Shift') {

      resetZoom();
    }
  };

  window.addEventListener(

    'keyup',

    handleKeyUp
  );

  return () => {

    window.removeEventListener(

      'keyup',

      handleKeyUp
    );
  };

}, []);

  React.useEffect(() => {
    const image = imageRef.current;

    if (!image || !image.complete || !image.naturalWidth || !image.naturalHeight) return;

    setImageAspectRatio(`${image.naturalWidth} / ${image.naturalHeight}`);
  }, [src]);

  return (
    <div className="relative w-full">
      {showResetButton && zoom > 1 && (
        <button
          onClick={resetZoom}
          className="absolute top-2 right-2 z-10 bg-dark-800 hover:bg-dark-700 text-white px-3 py-1 rounded-md text-sm font-medium transition-colors duration-200 shadow-lg"
          type="button"
        >
          Resetar zoom
        </button>
      )}

      <div
        ref={containerRef}
        className="
        w-full
        flex
        items-center
        justify-center
        overflow-hidden
        rounded-xl
        border
        border-dark-700
        bg-dark-950
        shadow-lg"
        onMouseLeave={handleContainerMouseLeave}
        style={{ pointerEvents: 'none', aspectRatio: imageAspectRatio }}
      >
        <img
          ref={imageRef}
          src={src}
          alt={alt}

         className={`
w-full
h-full
object-contain
rounded-xl
select-none
transition-transform
duration-200
ease-out
${getCursorStyle()}
`}
          
          style={{
            transform: `translate(${translateX}px, ${translateY}px) scale(${zoom})`,
            transformOrigin: transformOrigin,
            pointerEvents: 'auto',
          }}
          
          onMouseEnter={handleMouseEnterImage}
          onMouseLeave={handleMouseLeaveImage}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onDoubleClick={handleDoubleClick}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onLoad={handleImageLoad}
          draggable={false}
          onWheel={handleWheel}
        />
      </div>
    </div>
  );
};

export default ImageZoom;
